import { queryOptions, useQuery } from '@tanstack/react-query';
import { ActivityService } from '../../generated';
import { ApiError } from '../../../components/ApiLoadingGuard/ApiLoadingGuard';
import { AttributeChipData } from '../../../components/Prediction/types';
import { STALE_TIME_LIVE_VIEW } from '../../queryClient';
import { transformSoilClassification } from '../soilClassification';

export const activitySoilClassificationKeys = {
  base: () => ['activitySoilClassification'] as const,
  one: ({ jobsiteId, captureStartAt }: ActivitySoilClassificationOptionProps) =>
    [...activitySoilClassificationKeys.base(), jobsiteId, captureStartAt] as const,
};

export interface ActivitySoilClassificationOptionProps {
  jobsiteId: string;
  captureStartAt: string;
}

export function activitySoilClassificationOptions({
  jobsiteId,
  captureStartAt,
  enabled,
}: ActivitySoilClassificationOptionProps & { enabled: boolean }) {
  return queryOptions<AttributeChipData[], ApiError>({
    queryKey: activitySoilClassificationKeys.one({ jobsiteId, captureStartAt }),
    queryFn: async () => {
      if (!jobsiteId || !captureStartAt) {
        throw new Error('jobsiteId and captureStartAt are required');
      }

      const response = await ActivityService.activityControllerGetSoilClassification({
        path: {
          jobsiteId,
          capture_start_at: captureStartAt,
        },
      });

      if (response.data !== undefined) {
        return transformSoilClassification(response.data.output);
      } else {
        throw (response as any).error || new Error(response?.response?.statusText || 'Unknown error');
      }
    },
    enabled: !!jobsiteId && !!captureStartAt && enabled,
    staleTime: STALE_TIME_LIVE_VIEW,
  });
}

export function useActivitySoilClassification(props: ActivitySoilClassificationOptionProps & { enabled: boolean }) {
  return useQuery(activitySoilClassificationOptions(props));
}
